/**
 * TextHighlighting Utility
 * 
 * Provides text highlighting functionality with fuzzy matching support.
 * First attempts exact matching, then falls back to fuzzy matching using Levenshtein distance.
 */

export interface HighlightedSegment {
  text: string;
  highlight: boolean;
}

/**
 * Builds a list of text segments with highlight flags using fuzzy matching.
 * 
 * @param text - The text to highlight
 * @param query - The search query
 * @returns Array of segments with highlight information
 */
export function buildHighlightedSegments(
  text: string,
  query?: string
): HighlightedSegment[] {
  if (!text || !query?.trim()) {
    return [{ text, highlight: false }];
  }

  const qTokens = query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (!qTokens.length) {
    return [{ text, highlight: false }];
  }

  // Try exact matching first
  const exactPattern = new RegExp(
    `(${qTokens.map((t) => escapeRegExp(t)).join("|")})`,
    "gi"
  );

  const exactMatches = [...text.matchAll(exactPattern)];
  
  if (exactMatches.length > 0) {
    return buildSegmentsFromExactMatches(text, exactMatches);
  }

  // Fallback to fuzzy matching
  return buildSegmentsFromFuzzyMatch(text, qTokens);
}

/**
 * Builds segments from exact regex matches.
 * Handles overlapping matches by merging them into single highlighted segments.
 */
function buildSegmentsFromExactMatches(
  text: string,
  matches: RegExpExecArray[]
): HighlightedSegment[] {
  const segments: HighlightedSegment[] = [];
  let idx = 0;

  for (const match of matches) {
    const start = match.index!;
    const end = start + match[0].length;

    // Skip if this match overlaps with previously processed text
    if (start < idx) {
      continue;
    }

    // Add non-highlighted text before match
    if (start > idx) {
      segments.push({ text: text.substring(idx, start), highlight: false });
    }

    // Add highlighted match
    segments.push({ text: text.substring(start, end), highlight: true });
    idx = end;
  }

  // Add remaining non-highlighted text
  if (idx < text.length) {
    segments.push({ text: text.substring(idx), highlight: false });
  }

  return segments;
}

/**
 * Builds segments from fuzzy matching using Levenshtein distance.
 */
function buildSegmentsFromFuzzyMatch(
  text: string,
  qTokens: string[]
): HighlightedSegment[] {
  const wordRegex = /[\p{L}\p{N}]+/gu;
  const segments: HighlightedSegment[] = [];
  let cursor = 0;

  for (const match of text.matchAll(wordRegex)) {
    const start = match.index!;
    const end = start + match[0].length;
    const word = text.substring(start, end);
    const wLower = word.toLowerCase();

    if (start > cursor) {
      segments.push({ text: text.substring(cursor, start), highlight: false });
    }

    const shouldHighlight = qTokens.some((qt) => {
      const distance = levenshtein(wLower, qt);
      const maxLen = Math.max(wLower.length, qt.length);
      const allowed = allowedEditsForLength(maxLen);
      return distance <= allowed;
    });

    segments.push({ text: word, highlight: shouldHighlight });
    cursor = end;
  }

  if (cursor < text.length) {
    segments.push({ text: text.substring(cursor), highlight: false });
  }

  return segments;
}

/**
 * Slices text around the first match and trims to a window size.
 * Useful for showing snippets in search results.
 * Returns full text if no match found or query is empty.
 * 
 * @param raw - The raw text (always provided, may have whitespace/ellipsis)
 * @param query - Optional search query to match against
 * @returns Trimmed text around first match, or full text if no match
 */
export function sliceAroundFirstMatch(raw: string, query?: string): string {
  let text = raw.replace(/\s+/g, " ").trim();
  // Remove leading/trailing ellipsis
  text = text.replace(/^\s*(?:…|\.\.\.)\s*/u, "").replace(/\s*(?:…|\.\.\.)\s*$/u, "");

  if (!query?.trim()) {
    return text;
  }

  const qTokens = query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (!qTokens.length) {
    return text;
  }

  // Try exact match first
  // Note: We use .search() which only needs the first match position,
  // so we don't need the 'g' flag. The 'i' flag makes it case-insensitive.
  const exactRegex = new RegExp(
    `(${qTokens.map((t) => escapeRegExp(t)).join("|")})`,
    "i"
  );
  let matchIdx = text.search(exactRegex);

  if (matchIdx < 0) {
    matchIdx = findFuzzyMatchPosition(text, qTokens);
  }

  const CONTEXT_BEFORE = 10; // Context chars before match
  const MAX_SNIPPET_LENGTH = 180; // Max snippet length

  if (matchIdx >= 0) {
    const start = Math.max(0, matchIdx - CONTEXT_BEFORE);
    const end = Math.min(text.length, start + MAX_SNIPPET_LENGTH);
    const prefix = start > 0 ? "…" : "";
    const suffix = end < text.length ? "…" : "";
    return prefix + text.substring(start, end).trim() + suffix;
  }

  // No match found, return truncated text
  if (text.length > MAX_SNIPPET_LENGTH) {
    let cut = text.substring(0, MAX_SNIPPET_LENGTH);
    const lastSpace = cut.lastIndexOf(" ");
    if (lastSpace > 0) {
      cut = cut.substring(0, lastSpace);
    }
    return cut + "…";
  }

  return text;
}

/**
 * Finds the position of the first fuzzy match in text.
 */
function findFuzzyMatchPosition(text: string, qTokens: string[]): number {
  const wordRegex = /[\p{L}\p{N}]+/gu;
  let bestIdx = -1;

  for (const match of text.matchAll(wordRegex)) {
    const word = match[0].toLowerCase();
    const hasMatch = qTokens.some((qt) => {
      const distance = levenshtein(word, qt);
      const maxLen = Math.max(word.length, qt.length);
      const allowed = allowedEditsForLength(maxLen);
      return distance <= allowed;
    });

    if (hasMatch) {
      bestIdx = match.index!;
      break; // Return first match
    }
  }

  return bestIdx;
}

/**
 * Calculates the Levenshtein distance between two strings.
 * Returns the minimum number of single-character edits (insertions, deletions, or substitutions)
 * required to change one string into the other.
 * 
 * Optimized version using O(m) space where m = min(s.length, t.length) instead of O(m*n).
 * 
 * @param s - First string
 * @param t - Second string
 * @returns Levenshtein distance
 */
export function levenshtein(s: string, t: string): number {
  if (s === t) return 0;
  if (!s) return t.length;
  if (!t) return s.length;

  // Ensure s is the shorter string for less memory usage
  if (s.length > t.length) {
    [s, t] = [t, s];
  }

  let prevRow = Array.from({ length: s.length + 1 }, (_, i) => i);
  let currRow = new Array(s.length + 1).fill(0);

  for (let j = 1; j <= t.length; j++) {
    currRow[0] = j;

    for (let i = 1; i <= s.length; i++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      currRow[i] = Math.min(
        prevRow[i] + 1, // deletion
        currRow[i - 1] + 1, // insertion
        prevRow[i - 1] + cost // substitution
      );
    }

    // Swap rows for next iteration
    [prevRow, currRow] = [currRow, prevRow];
  }

  return prevRow[s.length];
}

/**
 * Determines the allowed number of edits based on string length.
 * Longer strings allow more edits for fuzzy matching.
 * 
 * Scaling:
 * - len 1-4: allow 1 edit (max distance for short strings)
 * - len 5-8: allow 2 edits
 * - len 9+: allow 3 edits (capped at 3 to prevent too loose matching)
 * 
 * @param len - Length of the string
 * @returns Maximum allowed edit distance (1-3)
 */
export function allowedEditsForLength(len: number): number {
  if (len <= 4) return 1;
  if (len <= 8) return 2;
  // For longer strings, calculate 25% but cap at 3 to prevent overly loose matching
  return Math.min(3, Math.round(len * 0.25));
}

/**
 * Escapes special regex characters.
 * 
 * @param str - String to escape
 * @returns Escaped regex string
 */
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
